import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { get } from "@/lib/api";
import { PageHeader, Section } from "@/components/common/Layout";
import { DataTable, Pagination } from "@/components/common/DataTable";
import { StatusBadge, ScoreBadge } from "@/components/common/StatusBadge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CALL_OUTCOMES, CALL_STATUSES, LANGUAGES, dt, duration, money, title } from "@/lib/format";
export default function Calls() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({
    q: "", status: "", outcome: "", language: "", campaign_id: "", date_from: "", date_to: "",
  });

  const params = { page, page_size: 25, ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v)) };
  const { data, isLoading } = useQuery({
    queryKey: ["calls", params],
    queryFn: () => get("/calls", params),
    refetchInterval: 8000,
  });
  const { data: campaigns } = useQuery({ queryKey: ["campaign-options"], queryFn: () => get("/campaigns", { page_size: 100 }) });

  const set = (key) => (value) => {
    setFilters((f) => ({ ...f, [key]: value === "all" ? "" : value }));
    setPage(1);
  };

  return (
    <>
      <PageHeader testId="calls-page" title="Calls" subtitle="Complete outbound call log with outcomes, costs and recordings" />

      <div className="panel grid grid-cols-1 gap-3 p-3 md:grid-cols-3 lg:grid-cols-6">
        <Input data-testid="calls-search" value={filters.q} onChange={(e) => set("q")(e.target.value)} placeholder="Contact or phone" className="h-9" />
        <Select value={filters.status || "all"} onValueChange={set("status")}>
          <SelectTrigger data-testid="calls-status-filter" className="h-9"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All statuses</SelectItem>
            {CALL_STATUSES.map((s) => <SelectItem key={s} value={s}>{title(s)}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.outcome || "all"} onValueChange={set("outcome")}>
          <SelectTrigger data-testid="calls-outcome-filter" className="h-9"><SelectValue placeholder="Outcome" /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All outcomes</SelectItem>
            {CALL_OUTCOMES.map((s) => <SelectItem key={s} value={s}>{title(s)}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.language || "all"} onValueChange={set("language")}>
          <SelectTrigger data-testid="calls-language-filter" className="h-9"><SelectValue placeholder="Language" /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All languages</SelectItem>
            {LANGUAGES.map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.campaign_id || "all"} onValueChange={set("campaign_id")}>
          <SelectTrigger data-testid="calls-campaign-filter" className="h-9"><SelectValue placeholder="Campaign" /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All campaigns</SelectItem>
            {(campaigns?.items || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex gap-2">
          <Input data-testid="calls-date-from" type="date" value={filters.date_from} onChange={(e) => set("date_from")(e.target.value)} className="h-9" />
          <Input data-testid="calls-date-to" type="date" value={filters.date_to} onChange={(e) => set("date_to")(e.target.value)} className="h-9" />
        </div>
      </div>

      <Section testId="calls-section" title={`${data?.total ?? 0} calls`}>
        <DataTable
          testId="calls-table"
          rowTestId="call-row"
          loading={isLoading}
          onRowClick={(r) => navigate(`/calls/${r.id}`)}
          columns={[
            { key: "contact_name", label: "Contact", render: (r) => <span className="font-medium text-slate-800">{r.contact_name || "—"}</span> },
            { key: "phone", label: "Phone", render: (r) => <span className="font-mono text-xs">{r.phone}</span> },
            { key: "campaign_name", label: "Campaign", render: (r) => (
              r.mode === "human"
                ? <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-emerald-700" data-testid={`call-human-${r.id}`}>Human</span>
                : (r.campaign_name || "—")
            ) },
            { key: "agent_name", label: "Agent", render: (r) => (r.mode === "human" ? `Operator · ${r.operator_name || "you"}` : `${r.agent_name || "—"} v${r.agent_version || 1}`) },
            { key: "language", label: "Lang", render: (r) => (r.language || "").toUpperCase() },
            { key: "country", label: "Country" },
            { key: "from_number", label: "From", render: (r) => <span className="font-mono text-xs">{r.from_number}</span> },
            { key: "provider", label: "Provider", render: (r) => title(r.provider) },
            { key: "started_at", label: "Started", render: (r) => dt(r.started_at) },
            { key: "duration_seconds", label: "Duration", render: (r) => duration(r.duration_seconds) },
            { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} /> },
            { key: "outcome", label: "Outcome", render: (r) => (r.outcome ? <StatusBadge value={r.outcome} /> : "—") },
            { key: "interest_score", label: "Score", render: (r) => <ScoreBadge score={r.interest_score} /> },
            { key: "total_cost", label: "Cost", render: (r) => money(r.total_cost) },
            { key: "recording_status", label: "Recording", render: (r) => <StatusBadge value={r.recording_status} /> },
          ]}
          rows={data?.items || []}
          empty={{ title: "No calls match these filters" }}
        />
        <Pagination page={data?.page || 1} pages={data?.pages || 1} total={data?.total || 0} onChange={setPage} />
      </Section>
    </>
  );
}